import React, { useState } from "react";
import InvestorOnboardingField from "./InvestorOnboardingField";

/**
 * One sub-section rendered as a box. Required fields are always visible; the rest
 * ("advanced") sit behind a toggle inside the same box so the default form stays short.
 * A field is "advanced" when it is not required (computed fields are always advanced).
 */

const TAG_META = {
  match: { label: "Matching", color: "#1565c0", bg: "#e3f2fd" },
  review: { label: "Need to know", color: "#8a5a1a", bg: "#fdf1de" },
  calc: { label: "Financing", color: "#8a6d1a", bg: "#f6ecd4" },
  crm: { label: "Internal", color: "#5a6472", bg: "#eef0f3" },
};

const isAdvanced = (f) => f.type === "computed" || !f.required;

export default function InvestorOnboardingBox({ box, form, setField, neighborhoods, teamMembers }) {
  const advancedFields = box.fields.filter(isAdvanced);
  const basicFields = box.fields.filter((f) => !isAdvanced(f));
  const allAdvanced = basicFields.length === 0;
  const [open, setOpen] = useState(allAdvanced); // boxes with no required fields start expanded

  const advancedFilledCount = advancedFields.filter((f) => {
    const v = form[f.key];
    return Array.isArray(v) ? v.length > 0 : v != null && v !== "";
  }).length;

  const tag = TAG_META[box.tag] || TAG_META.crm;

  const renderField = (f) => (
    <div key={f.key} style={{ gridColumn: ["textarea", "neighborhoods", "buildings", "multiselect", "boroughs", "members", "keylocations"].includes(f.type) ? "1 / -1" : "auto" }}>
      <InvestorOnboardingField
        field={f}
        value={form[f.key]}
        onChange={(v) => setField(f.key, v, f)}
        neighborhoods={neighborhoods}
        teamMembers={teamMembers}
        form={form}
      />
    </div>
  );

  return (
    <div style={{
      backgroundColor: "white", border: "1px solid #e6e9ee", borderRadius: "12px",
      padding: "20px 22px", boxShadow: "0 1px 3px rgba(20,27,34,0.05)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: basicFields.length ? "18px" : (open ? "18px" : "0") }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#1a1f26" }}>{box.title}</h3>
          <span style={{
            fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
            color: tag.color, backgroundColor: tag.bg, padding: "3px 8px", borderRadius: "20px",
          }}>{tag.label}</span>
        </div>
      </div>

      {box.note && (
        <div style={{ fontSize: "12px", color: "#8a929b", marginBottom: "14px", marginTop: "-8px" }}>{box.note}</div>
      )}

      {basicFields.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
          {basicFields.map(renderField)}
        </div>
      )}

      {advancedFields.length > 0 && !allAdvanced && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            marginTop: basicFields.length ? "16px" : 0, background: "none", border: "none",
            color: "#1976d2", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: "4px 0",
            display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <span style={{ fontSize: "11px" }}>{open ? "▼" : "▶"}</span>
          {open ? "Hide advanced" : "Advanced"}
          <span style={{ color: "#9aa3ad", fontWeight: 500 }}>
            ({advancedFields.length}{advancedFilledCount > 0 ? ` · ${advancedFilledCount} set` : ""})
          </span>
        </button>
      )}

      {advancedFields.length > 0 && open && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px",
          marginTop: allAdvanced ? 0 : "16px",
          paddingTop: allAdvanced ? 0 : "16px",
          borderTop: allAdvanced ? "none" : "1px dashed #e6e9ee",
        }}>
          {advancedFields.map(renderField)}
        </div>
      )}
    </div>
  );
}
